import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Minimize2, Volume2, VolumeX, Wifi, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatComposer } from "./ChatComposer";
import { ConversationList } from "./ConversationList";
import { MessageThread } from "./MessageThread";
import { NewConversationModal } from "./NewConversationModal";
import { conversationTitle } from "./chatUtils";
import { playNotificationSound, unlockNotificationSound } from "./notificationSound";

export function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("kloss_chat_sound") !== "off");
  const selectedRef = useRef(null);
  const openRef = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);
  const bottomRef = useRef(null);

  const selected = useMemo(() => conversations.find((c) => c.id === selectedId), [conversations, selectedId]);
  const unread = useMemo(() => conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0), [conversations]);
  const userId = user?.id;

  const fetchConversations = useCallback(async () => {
    const { data } = await api.get("/chat/conversations");
    setConversations(data);
    setSelectedId((current) => current || data[0]?.id || null);
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    const { data } = await api.get(`/chat/conversations/${conversationId}/messages`);
    setMessages(data);
    await api.post(`/chat/conversations/${conversationId}/read`);
    fetchConversations();
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 60);
  }, [fetchConversations]);

  useEffect(() => {
    if (user) fetchConversations().catch(() => {});
  }, [user, fetchConversations]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    localStorage.setItem("kloss_chat_sound", soundEnabled ? "on" : "off");
  }, [soundEnabled]);

  useEffect(() => {
    selectedRef.current = selectedId;
    if (selectedId && open) fetchMessages(selectedId).catch(() => toast.error("Não foi possível carregar a conversa"));
  }, [selectedId, open, fetchMessages]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!userId) return undefined;
    const currentUserId = userId;
    const token = localStorage.getItem("kloss_token");
    if (!token || !process.env.REACT_APP_BACKEND_URL) return undefined;
    let ws;
    let retry;
    let active = true;
    const connect = () => {
      if (!active) return;
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws").replace(/\/$/, "");
      ws = new WebSocket(`${base}/api/chat/ws?token=${encodeURIComponent(token)}`);
      ws.onopen = () => {
        if (!active) {
          ws.close(1000, "cleanup");
          return;
        }
        setConnected(true);
      };
      ws.onclose = () => {
        if (!active) return;
        setConnected(false);
        retry = setTimeout(connect, 5000);
      };
      ws.onerror = () => {
        if (active) setConnected(false);
      };
      ws.onmessage = (event) => {
        if (!active) return;
        const payload = JSON.parse(event.data || "{}");
        if (payload.type === "message.created") {
          const incoming = payload.message?.sender_id !== currentUserId;
          if (incoming && soundEnabledRef.current) {
            playNotificationSound().catch(() => {});
          }
          fetchConversations().catch(() => {});
          if (payload.conversation_id === selectedRef.current && openRef.current) {
            setMessages((prev) => prev.some((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]);
            api.post(`/chat/conversations/${payload.conversation_id}/read`).catch(() => {});
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 60);
          } else if (incoming) {
            toast.info(`Nova mensagem de ${payload.message?.sender_name || "equipe"}`);
          }
        }
        if (payload.type === "message.updated") {
          fetchConversations().catch(() => {});
          if (payload.conversation_id === selectedRef.current) {
            setMessages((prev) => prev.map((m) => m.id === payload.message.id ? payload.message : m));
          }
        }
        if (payload.type === "message.deleted") {
          fetchConversations().catch(() => {});
          if (payload.conversation_id === selectedRef.current) {
            setMessages((prev) => prev.filter((m) => m.id !== payload.message_id));
          }
        }
        if (payload.type === "conversation.deleted") {
          setConversations((prev) => {
            const next = prev.filter((c) => c.id !== payload.conversation_id);
            if (payload.conversation_id === selectedRef.current) {
              setSelectedId(next[0]?.id || null);
              setMessages([]);
            }
            return next;
          });
        }
        if (payload.type === "conversation.updated" || payload.type === "conversation.read") {
          fetchConversations().catch(() => {});
        }
      };
    };
    connect();
    return () => {
      active = false;
      setConnected(false);
      clearTimeout(retry);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => ws.close(1000, "cleanup");
        } else if (ws.readyState === WebSocket.OPEN) {
          ws.close(1000, "cleanup");
        }
      }
    };
  }, [userId, fetchConversations]);

  const openNew = async () => {
    try {
      const { data } = await api.get("/chat/users");
      setUsers(data);
      setCreating(true);
    } catch (e) {
      toast.error("Não foi possível carregar os usuários");
    }
  };

  const createConversation = async (payload) => {
    try {
      const { data } = await api.post("/chat/conversations", payload);
      await fetchConversations();
      setSelectedId(data.id);
      setCreating(false);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível criar a conversa");
    }
  };

  const sendMessage = async (text, files) => {
    const form = new FormData();
    form.append("text", text || "");
    files.forEach((file) => form.append("files", file));
    const { data } = await api.post(`/chat/conversations/${selectedId}/messages`, form, { headers: { "Content-Type": "multipart/form-data" } });
    setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
    fetchConversations();
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 60);
  };

  const editMessage = async (messageId, text) => {
    try {
      const { data } = await api.patch(`/chat/messages/${messageId}`, { text });
      setMessages((prev) => prev.map((m) => m.id === messageId ? data : m));
      fetchConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível editar a mensagem");
      throw e;
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Apagar esta mensagem para todos?")) return;
    try {
      await api.delete(`/chat/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      fetchConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível apagar a mensagem");
    }
  };

  const deleteConversation = async (conversation) => {
    const title = conversationTitle(conversation, user.id);
    if (!window.confirm(`Apagar a conversa "${title}" para todos?`)) return;
    try {
      await api.delete(`/chat/conversations/${conversation.id}`);
      const next = conversations.filter((c) => c.id !== conversation.id);
      setConversations(next);
      setSelectedId(next[0]?.id || null);
      setMessages([]);
      toast.success("Conversa apagada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível apagar a conversa");
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="chat-floating-toggle-button"
          onClick={() => { setOpen(true); if (soundEnabled) unlockNotificationSound().catch(() => {}); }}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl text-white shadow-xl grid place-items-center hover:-translate-y-0.5 transition-[transform,background-color,box-shadow] ${unread > 0 ? "bg-[#C2410C] hover:bg-[#9A3412] shadow-[0_18px_40px_rgba(194,65,12,0.35)] ring-4 ring-[#FED7AA]" : "bg-[#0B2230] hover:bg-[#143346]"}`}
          style={{ backgroundColor: unread > 0 ? "#C2410C" : undefined }}
          aria-label="Abrir mensagens"
        >
          <MessageSquare data-testid="chat-floating-icon" className={`w-6 h-6 ${unread > 0 ? "text-[#FFF7ED]" : "text-white"}`} />
          {unread > 0 && <span data-testid="chat-floating-unread-badge" className="absolute -top-1 -right-1 min-w-6 h-6 px-1 rounded-full bg-[#0B2230] text-white text-xs grid place-items-center border-2 border-white animate-pulse">{unread}</span>}
        </button>
      )}

      {open && (
        <div data-testid="chat-floating-panel" className="fixed bottom-5 right-5 z-50 w-[min(900px,calc(100vw-40px))] h-[min(680px,calc(100vh-40px))] bg-white rounded-2xl shadow-2xl border border-[#DCD7CE] overflow-hidden flex flex-col">
          <div className="h-12 px-4 bg-[#0B2230] text-white flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <MessageSquare className="w-4 h-4" />
              <span data-testid="chat-panel-title" className="font-heading font-semibold truncate">{selected ? conversationTitle(selected, user.id) : "Mensagens internas"}</span>
              <span data-testid="chat-connection-status" className="text-[11px] text-white/65 inline-flex items-center gap-1">
                {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {connected ? "online" : "reconectando"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chat-sound-toggle-button"
                onClick={() => {
                  const next = !soundEnabled;
                  setSoundEnabled(next);
                  if (next) unlockNotificationSound().catch(() => {});
                }}
                className="w-8 h-8 rounded-lg hover:bg-white/10 grid place-items-center"
                aria-label={soundEnabled ? "Desativar som do chat" : "Ativar som do chat"}
                title={soundEnabled ? "Som ligado" : "Som desligado"}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button data-testid="chat-panel-minimize-button" onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg hover:bg-white/10 grid place-items-center" aria-label="Minimizar chat"><Minimize2 className="w-4 h-4" /></button>
              <button data-testid="chat-panel-close-button" onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg hover:bg-white/10 grid place-items-center" aria-label="Fechar chat"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex-1 min-h-0 min-w-0 grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)]">
            <ConversationList conversations={conversations} currentUserId={user.id} selectedId={selectedId} onSelect={setSelectedId} onNew={openNew} />
            <div className="min-h-0 min-w-0 flex flex-col overflow-hidden">
              <MessageThread
                conversation={selected}
                messages={messages}
                currentUserId={user.id}
                bottomRef={bottomRef}
                onEditMessage={editMessage}
                onDeleteMessage={deleteMessage}
                onDeleteConversation={deleteConversation}
              />
              <ChatComposer disabled={!selectedId} onSend={sendMessage} />
            </div>
          </div>
        </div>
      )}

      {creating && <NewConversationModal users={users} currentUserId={user.id} onClose={() => setCreating(false)} onCreate={createConversation} />}
    </>
  );
}